<script>
import {useFilterStore} from '@store/filterStore';
import {useTabStore} from '@store/tabStore';

/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView';
import GaListView from '@components/v2/layout/GaListView';
import AkListFilter from '@components/v2/filter/AkListFilter';
import AkAutocompleteFilter from '@components/v2/filter/AkAutocompleteFilter';
import AkInputFilter from '@components/v2/filter/AkInputFilter';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination';
import AkDatatable from '@components/v2/general/AkDatatable.vue';
import AkAllFilter from '@components/v2/filter/AkAllFilter';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';

/* MIXINS */
import accommodationTypeConst from '@mixins/const/accommodationTypeConst';
import loaderMixin from '@mixins/loaderMixin';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';
import messageMixin from '@mixins/messageMixin';
import reservationTypesConst from '@mixins/const/reservationTypesConst';
import sidebarMixin from '@mixins/sidebarMixin';
import smilyStatusConst from '@mixins/const/smilyStatusConst';
import utilsMixin from '@mixins/utilsMixin';
import ownerAutocompleteMixin from '@mixins/autocomplete/ownerAutocompleteMixin';
import reservationAutocompleteMixin from '@mixins/autocomplete/reservationAutocompleteMixin';
import reservationSiteConst from '@mixins/const/reservationSiteConst';
import accommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';
import companyMixin from '@mixins/companyMixin';
import filterListMixin from '@mixins/filterListMixin';

/* SERVICES */
import reservationService from '@services/reservationService';
import reservationExportService from '@services/reservationExportService';
import accommodationService from '@services/accommodationService';
import GaLayoutEmptyTab from '@components/v2/general/GaLayoutEmptyTab.vue';
import mainTabMixin from '@mixins/mainTabMixin';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';
import countdownMixin from '@mixins/const/countdownMixin';
import OpportuniteDetailSidebar from '@views/opportunites/OpportuniteDetailSidebar.vue';

export default {
  components: {
    OpportuniteDetailSidebar,
    AkButtonValidation,
    GaLayoutEmptyTab,
    AkButtonAction,
    AkAllFilter,
    AkDatatablePagination,
    AkDatatable,
    AkInputFilter,
    AkAutocompleteFilter,
    AkListFilter,
    GaListView,
    GaView,
  },
  provide: {
    threadActionOrigin: 'reservation_list',
  },
  mixins: [
    accommodationTypeConst,
    loaderMixin,
    permissionsMixinComputed,
    messageMixin,
    reservationTypesConst,
    sidebarMixin,
    utilsMixin,
    smilyStatusConst,
    ownerAutocompleteMixin,
    reservationAutocompleteMixin,
    reservationSiteConst,
    accommodationAutocompleteMixin,
    companyMixin,
    filterListMixin,
    mainTabMixin,
    countdownMixin,
  ],
  head() {
    return {
      title: this.isOwner ? 'menu.my_reservations' : 'reservation.list_long',
    };
  },
  data() {
    return {
      loading: true,
      debounceFn: undefined,
      filter: {
        page: 1,
        limit: 20,
        sort: [],
      },
      list: [],
      totalItems: 0,
      item: {},
      activeTab: 2,
      hasNonPremiumAccommodation: false,
    };
  },
  mounted() {
    this.activeTab = useTabStore().getTabActive('ReservationsList', 2);
    let filter = useFilterStore().getFilter();
    if (filter) {
      this.filter = {...this.filter, ...filter};
    } else {
      this.getFilterFromTab();
    }
    if (this.isOwner) this.loadOwnerOffers();
  },
  methods: {
    loadOwnerOffers() {
      accommodationService.resume({enabled: true}).then((accommodations) => {
        this.hasNonPremiumAccommodation = accommodations.some((accommodation) => accommodation.offerPremium === false);
        if (!this.showOpportunitiesTab && this.activeTab === 5) this.changeActiveTab(2);
      });
    },
    exportReservations() {
      this.showTotalLoaderWithAfter(this.$t('export_in_progress'));
      reservationExportService
        .exportReservations(this.filter)
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => this.hideLoader());
    },
    search() {
      this.loading = true;
      reservationService.reservationsPagination(this.filter).then((data) => {
        this.list = data.data;
        this.totalItems = data.totalItems;
        this.loading = false;
      });
    },
    getFilterFromTab() {
      this.filter.filterTab = undefined;
      this.filter.sort = [];

      if (this.activeTab === 1) {
        this.filter.filterTab = 'A_VENIR';
        this.filter.sort.push('date_checkin:ASC');
      } else if (this.activeTab === 2) {
        this.filter.filterTab = 'EN_COURS';
        this.filter.sort.push('date_checkin:ASC');
      } else if (this.activeTab === 3) {
        this.filter.filterTab = 'PASSE';
        this.filter.sort.push('date_checkout:DESC');
      } else if (this.activeTab === 4) {
        this.filter.filterTab = 'ANNULE';
        this.filter.sort.push('date_cancel:DESC');
      } else if (this.activeTab === 5) {
        this.filter.filterTab = 'OPPORTUNITES';
        this.filter.sort.push('date_query_expiry:ASC');
      } else {
        this.filter.sort.push('date_created:DESC');
      }
    },
    displayPeriodForReservation(data) {
      return this.displayDatesSeparator(data.dateStart, data.dateEnd, false);
    },
    isReservationQueryRow(data) {
      return data.statusSmily === 'Tentative' || ['denied', 'retracted', 'timeout'].includes(data.queryStatus);
    },
    rowClick(event) {
      let data = event.data;
      if (this.isReservationQueryRow(data)) {
        if (!data.threadId) {
          this.addError('Impossible de charger la conversation : threadId manquant.');
          return;
        }
        this.$refs.opportuniteDetailSidebar.open(data.threadId);
      } else {
        data.header = data.localizer;
        this.toggleSidebar('GaReservationSidebar', 'Détails de la réservation', data.id, this.deleteReservationHote);
      }
    },
    deleteReservationHote(success) {
      this.hideSidebar();
      if (success) {
        this.success(this.$t('unavailability.deleted'));
        this.search();
      } else {
        this.addError(this.$t('unavailability.impossible_to_cancel'));
      }
    },

    // LITIGE

    getLitigeStatusTooltip(data) {
      let status = '[' + this.displayDates(data.dateStartLitige, data.dateEndLitige) + ']';
      if (data.amountLitigeEstimated)
        status += ' - ' + this.formatCurrencyEUR(this.formatAmount(data.amountLitigeEstimated));
      return status;
    },

    getLitigeDisplay(data) {
      return this.$t(data.dateStartLitige ? 'reservation.endLitige' : 'reservation.startLitige');
    },

    openLitigeSidebar(data) {
      this.toggleSidebar('GaReservationLitigeSidebar', this.getLitigeDisplay(data), data.id, this.createLitige);
    },

    createLitige(success, dateEndLitige) {
      if (success) {
        this.success(this.$t(dateEndLitige ? 'reservation.litigeEnded' : 'reservation.litigeStarted'));
        this.search();
      } else {
        this.addError(this.$t('error.error'));
      }
    },

    // RECLAMATION

    getReclamationStatusTooltip(data) {
      let status = '[' + this.displayDates(data.dateStartReclamation, data.dateEndReclamation) + ']';
      if (data.amountReclamationEstimated)
        status += ' - ' + this.formatCurrencyEUR(this.formatAmount(data.amountReclamationEstimated));
      return status;
    },
    getReclamationDisplay(data) {
      return this.$t(data.dateStartReclamation ? 'reservation.endReclamation' : 'reservation.startReclamation');
    },

    openReclamationSidebar(data) {
      this.toggleSidebar(
        'GaReservationReclamationSidebar',
        this.getReclamationDisplay(data),
        data.id,
        this.createReclamation,
      );
    },

    createReclamation(success, dateEndReclamation) {
      if (success) {
        this.success(this.$t(dateEndReclamation ? 'reservation.reclamationEnded' : 'reservation.reclamationStarted'));
        this.search();
      } else {
        this.addError(this.$t('error.error'));
      }
    },

    handleReclamation() {
      this.v$.itemReclamation.$touch();
      if (this.v$.itemReclamation.$error) {
        this.$refs.dialogReclamation.init();
        return;
      }

      let reclamation = {...this.itemReclamation};

      if (reclamation.dateStartReclamation) {
        let total = reclamation.amountReclamationReported;
        let totalGas = reclamation.amountReclamationGas;
        let totalGav = reclamation.amountReclamationGav;
        let totalOwner = reclamation.amountReclamationOwner;

        if (total) {
          if (this.addNumbers([totalGas, totalGav, totalOwner]) !== total) {
            this.$refs.dialogReclamation.error("Le total des parts n'est pas égal au montant reversé");
            return;
          }
        }
        reclamation.dateEndReclamation = new Date(reclamation.dateReclamation);
      } else reclamation.dateStartReclamation = new Date(reclamation.dateReclamation);
      reclamation.dateReclamation = null;

      reservationService
        .updateReclamation(reclamation)
        .then((data) => {
          this.success(this.$t(reclamation ? 'reservation.reclamationEnded' : 'reservation.reclamationStarted'));
        })
        .finally(() => {
          this.$refs.dialogReclamation.hide();
          this.search();
        });
    },
    openFilterPanel() {
      this.toggleFilterTitle(
        'GaReservationFilterList',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    resetFilter() {
      this.filter = {
        page: 1,
        limit: 20,
        sort: ['id:DESC'],
      };
      this.search();
      this.toggleFilterTitle(
        'GaReservationFilterList',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    displaySublineStatus(r) {
      if (r.type === 'OWNER' || r.type === 'OWNER_CANCEL') return 'Hôte';
      if (r.type === 'BLOCKADE' || r.type === 'BLOCKADE_CANCEL') return 'Indisponible';
      return 'Voyageur';
    },
    changeActiveTab(tab) {
      this.activeTab = tab;
      useTabStore().setTabActive('ReservationsList', tab);
      this.getFilterFromTab();
    },
  },
  computed: {
    showOpportunitiesTab() {
      return !this.isOwner || this.hasNonPremiumAccommodation;
    },
  },
  watch: {
    activeTab: function () {
      this.filter.page = 1;
    },
  },
};
</script>

<template>
  <GaView v-show="this.hasAccommodation || !this.isOwner">
    <template #action>
      <div class="flex justify-end gap-4">
        <AkButtonAction secondary :label="$t('download')" little @click="exportReservations()" />
      </div>
    </template>
    <template #content>
      <GaListView searchable @search="this.searchQuery">
        <template #tabs>
          <div class="flex items-center">
            <span
              v-if="showOpportunitiesTab"
              class="list-table-item"
              @click="this.changeActiveTab(5)"
              :class="[{active: this.activeTab === 5}]">
              Opportunités
            </span>
            <span class="list-table-item" @click="this.changeActiveTab(1)" :class="[{active: this.activeTab === 1}]">
              À venir
            </span>
            <span class="list-table-item" @click="this.changeActiveTab(2)" :class="[{active: this.activeTab === 2}]">
              En cours
            </span>
            <span class="list-table-item" @click="this.changeActiveTab(3)" :class="[{active: this.activeTab === 3}]">
              Passées
            </span>
            <span class="list-table-item" @click="this.changeActiveTab(4)" :class="[{active: this.activeTab === 4}]">
              Annulées
            </span>
            <span class="list-table-item" @click="this.changeActiveTab(6)" :class="[{active: this.activeTab === 6}]">
              Toutes
            </span>
          </div>
        </template>
        <template #filter>
          <AkListFilter
            v-if="this.isGas"
            :multi-selection="true"
            v-model="this.filter.companyIds"
            :items="this.companies"
            :display-filter="true"
            @search="this.clickSearch"
            :label="$t('employee.company')"
            direction="right" />
          <AkInputFilter
            v-model="this.filter.guestName"
            @search="this.clickSearch"
            label="Voyageur"
            direction="right" />
          <AkAutocompleteFilter
            v-model="this.filter.reservationId"
            @search="this.clickSearch"
            label="Réservation"
            item-value="id"
            item-display="display"
            direction="right"
            :minLengthToLaunchSearch="3"
            :init-method="this.autocompleteReservationById"
            :search-method="this.autocompleteReservation" />

          <AkAllFilter @open="this.openFilterPanel" />
        </template>
        <template #content>
          <AkDatatablePagination
            :key="'dt-' + activeTab"
            :lazy="!this.isHousekeeper"
            :loading="loading"
            :value="list"
            :total-records="this.totalItems"
            @row-click="rowClick($event)"
            @search="this.search"
            :filter="this.filter">
            <template v-if="activeTab === 5" #empty>Aucune opportunité</template>
            <template v-else #empty>
              {{ $t('reservation.empty') }}
            </template>
            <Column
              :header="$t('reservation.reservation_site')"
              header-class="text-center"
              style="min-width: 110px; width: 110px; text-align: center">
              <template #body="slotProps">
                <span
                  class="logo-site"
                  :class="iconReservation(slotProps.data.reservationSite)"
                  v-tooltip.bottom="displayReservationSite(slotProps.data.reservationSite)" />
              </template>
            </Column>
            <Column
              :header="$t('reservation.localizer')"
              style="min-width: 200px; width: 200px"
              :sortable="true"
              sort-field="localizer">
              <template #body="slotProps">
                <router-link
                  v-if="this.isGasGav"
                  :to="{
                    name: 'reservationPage',
                    params: {reservationId: slotProps.data.id},
                  }">
                  {{
                    slotProps.data.localizer
                      ? slotProps.data.localizer
                      : slotProps.data.bookingCode + '-' + slotProps.data.agentLocalizator
                  }}
                </router-link>
                <span v-else class="text-secondary font-semibold text-base cursor-pointer hover:text-secondary">
                  {{
                    slotProps.data.localizer
                      ? slotProps.data.localizer
                      : slotProps.data.bookingCode + '-' + slotProps.data.agentLocalizator
                  }}
                </span>
              </template>
            </Column>

            <Column
              :header="$t('reservation.accommodation')"
              style="min-width: 250px; width: 250px"
              :sortable="true"
              sort-field="j_accommodation_name">
              <template #body="slotProps">
                <span v-if="this.canAccessAccommodation(slotProps.data.accommodationId)" class="pointer">
                  <router-link
                    :to="{name: 'accommodationPage', params: {accommodationId: slotProps.data.accommodationId}}">
                    {{ slotProps.data.accommodationName }}
                  </router-link>
                </span>
                <span v-else class="text-base">
                  {{ slotProps.data.accommodationName }}
                </span>
              </template>
            </Column>

            <Column :header="$t('reservation.guest')" style="width: 250px">
              <template #body="slotProps">
                <router-link
                  v-if="slotProps.data.clientId && this.isGasGav"
                  :to="{
                    name: 'clientPage',
                    params: {clientId: slotProps.data.clientId},
                  }">
                  <span v-tooltip.bottom="slotProps.data.guestEmail" class="font-semibold">
                    {{ slotProps.data.guestDisplay }}
                  </span>
                </router-link>
                <span v-tooltip.bottom="slotProps.data.guestEmail" v-else>
                  {{ slotProps.data.guestDisplay }}
                </span>
              </template>
            </Column>

            <Column :header="$t('reservation.type')" style="width: 150px" sortable sort-field="status_smily">
              <template #body="slotProps">
                <span>
                  {{ displayStatusSmilyDetailed(slotProps.data) }}
                  <br />
                  <span class="text-sm">{{ displaySublineStatus(slotProps.data) }}</span>
                </span>
              </template>
            </Column>
            <Column
              :header="$t('reservation.creation_date')"
              style="width: 200px"
              :sortable="true"
              sort-field="date_created">
              <template #body="slotProps">
                <span class="pointer">
                  {{ displayDateTime(slotProps.data.dateCreated) }}
                </span>
                <br />
                <span class="text-sm" v-if="this.activeTab === 4">
                  {{ displayDateTime(utcToLocal(slotProps.data.dateCancel)) }}
                </span>
              </template>
            </Column>
            <Column :header="$t('reservation.period')" style="width: 115px" :sortable="true" sort-field="date_checkin">
              <template #body="slotProps">
                <span class="pointer text-wrap">
                  {{ displayPeriodForReservation(slotProps.data) }}
                </span>
              </template>
            </Column>

            <Column v-if="activeTab === 5 || activeTab === 6" header="DÉLAI" style="width: 80px">
              <template #body="slotProps">
                <span
                  v-if="formatCountdown(slotProps.data.expiry) && slotProps.data.statusSmily === 'Tentative'"
                  class="text-red bg-red-100 rounded-full px-2 text-[11px] font-semibold flex gap-1 items-center">
                  <i class="ga-icon ga-clock text-sm" />
                  {{ formatCountdown(slotProps.data.expiry) }}
                </span>
                <span v-else class="text-grey-300">—</span>
              </template>
            </Column>

            <Column
              :header="$t('reservation.managementStatus')"
              style="width: 300px"
              v-if="this.isGasGav && activeTab !== 5">
              <template #body="slotProps">
                <div>
                  <div v-if="slotProps.data.dateStartLitige" :title="getLitigeStatusTooltip(slotProps.data)">
                    <span>
                      {{ $t(slotProps.data.dateEndLitige ? 'reservation.litigeEnded' : 'reservation.litigeStarted') }}
                    </span>
                  </div>
                  <div v-if="slotProps.data.dateStartReclamation" :title="getReclamationStatusTooltip(slotProps.data)">
                    <span>
                      {{
                        $t(
                          slotProps.data.dateEndReclamation
                            ? 'reservation.reclamationEnded'
                            : 'reservation.reclamationStarted',
                        )
                      }}
                    </span>
                  </div>
                </div>
              </template>
            </Column>

            <Column
              :header="$t('reservation.action')"
              style="width: 200px; min-width: 200px"
              header-class="text-center"
              v-if="this.isGasGav && activeTab !== 5">
              <template #body="slotProps">
                <div v-if="!isReservationQueryRow(slotProps.data)" class="flex gap-2 totalItems-center">
                  <button
                    class="btn-table flex totalItems-center justify-center"
                    @click="openLitigeSidebar(slotProps.data)"
                    v-if="!slotProps.data.dateEndLitige">
                    {{ getLitigeDisplay(slotProps.data) }}
                  </button>

                  <button
                    class="btn-table flex totalItems-center justify-center"
                    @click="openReclamationSidebar(slotProps.data)"
                    v-if="!slotProps.data.dateEndReclamation">
                    {{ getReclamationDisplay(slotProps.data) }}
                  </button>
                </div>
                <span v-else class="text-grey-300">—</span>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>
  </GaView>
  <ga-layout-empty-tab
    v-if="!this.hasAccommodation && this.isOwner"
    icon="ga-book"
    title="Mes __réservations__"
    gradient="linear-gradient(109deg, rgba(214, 176, 81, 0.20) 1.88%, rgba(255, 225, 150, 0.00) 44.31%), linear-gradient(0deg, #FFF7E5 0%, #FFF7E5 100%)"
    background-color="rgba(255, 247, 229, 1)"
    @init="init">
    <div class="flex flex-col grow items-center md:gap-8 gap-4 md:p-4 p-8 pb-0 md:min-w-[45%]">
      <div class="border-[7px] border-[#FFE197] rounded-full flex items-center justify-center w-[70px] h-[70px]">
        <i class="ga-icon ga-book text-[30px] text-grey-900" />
      </div>
      <div class="flex flex-col gap-6 items-center">
        <div class="text-base text-grey-900 text-center">
          <div class="font-semibold text-lg">Retrouvez ici vos réservations !</div>
          Publiez votre logement sur Guestadom et générez vos premières réservations !
        </div>
        <AkButtonValidation
          class="btn-primary !h-[48px] w-fit !px-8 font-medium"
          @click="goToOnboarding"
          ref="onboardingButton"
          :label="hasPendingOnboarding ? 'Reprendre la publication' : 'Démarrer la publication'" />
      </div>
    </div>
    <img v-if="$isMobile()" src="@refonte/img/emptyTabs/reservation_mobile_illustration.png" alt="" class="w-full" />
    <div v-else class="h-full md:max-w-[55%] w-full flex items-end justify-end">
      <img src="@refonte/img/emptyTabs/reservation_illustration.png" alt="" class="h-auto object-cover object-left" />
    </div>
  </ga-layout-empty-tab>
  <OpportuniteDetailSidebar
    ref="opportuniteDetailSidebar"
    show-status-banner
    @update="search"
    :show-staff-links="isGasGav" />
</template>
